<script>
    import { get } from 'lodash';
    import $ from 'bianco.query';
    import { isIE } from '@dreipol/vue-ui/src/util/detect/ie-detect';
    import { FORM_SUBMISSION_STATE } from 'util/vue/form';
    import {
        createGeneralErrors,
        createSubmissionRequest,
    } from 'framework/global-components/form-data-provider/helpers';
    import axios from 'util/service';

    export default {
        inheritAttrs: false,
        props: {
            dynamicFormFieldsApiUrl: {
                type: String,
                default: '',
            },
            formId: {
                type: String,
                default: '',
            },
            // eslint-disable-next-line vue/prop-name-casing
            success_url: {
                type: String,
                default: null,
            },
            url: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                submissionRequest: null,
                submissionState: FORM_SUBMISSION_STATE.MOUNTING,
                successUrl: null,
                fieldErrors: [],
                generalErrors: [],
            };
        },
        methods: {
            /**
             * Determine what values will be given to a form component
             * @return {IEnhancedForm} The enhanced form data
             */
            bindFormProps() {
                return {
                    action: this.url,
                    fieldErrors: this.fieldErrors,
                    generalErrors: this.generalErrors,
                    onFormSubmit: this.onFormSubmit,
                    resetFormData: this.resetFormData,
                    updateFormData: this.updateFormData,
                    submissionRequest: this.submissionRequest,
                    submissionState: this.submissionState,
                    successUrl: this.successUrl,
                    ...this.$attrs,
                };
            },
            /**
             * Handle a form submit request
             * @param {function[]} formDataPostProcessors - Post processors to change form data before submission
             */
            onFormSubmit(formDataPostProcessors = []) {
                if (this.submissionState === FORM_SUBMISSION_STATE.SUBMITTING) {
                    return;
                }

                // check if the root tag is a form otherwise query its children
                const [form] = this.$el.tagName === 'FORM' ? [this.$el] : $('form', this.$el);

                const postProcessors = Array.isArray(formDataPostProcessors) ? formDataPostProcessors : [];
                this.submissionRequest = createSubmissionRequest(form, this.url, postProcessors);
                this.submissionRequest.then(this.onSubmitSuccess, this.onSubmitError);

                this.submissionState = FORM_SUBMISSION_STATE.SUBMITTING;
            },
            /**
             * Handle a successful form submission
             * @param {object} response - The form response
             * @param {object} response.data - The response payload
             */
            onSubmitSuccess({ data }) {
                this.fieldErrors = get(data, 'errors', {});
                this.generalErrors = get(data, 'formErrors', []);

                this.successUrl = data.returnUrl || this.success_url;
                this.submissionState = FORM_SUBMISSION_STATE.SUCCESS;

                if (!this.fieldErrors.length && this.successUrl) {
                    window.location.href = this.successUrl;
                }
            },
            /**
             * Handle a failed form submission
             * @param {object} error - The form error
             * @param {object} error.response - An error response detail
             */
            onSubmitError({ response }) {
                this.fieldErrors = get(response, 'data.errors', {});
                this.generalErrors = createGeneralErrors(response);

                this.submissionState = FORM_SUBMISSION_STATE.ERROR;
            },
            /**
             * Reset the form, for instance when the submission was successful
             */
            resetFormData() {
                this.submissionState = FORM_SUBMISSION_STATE.READY;
            },
            /**
             * Update the form and force a data update
             */
            updateFormData() {
                this.submissionState = FORM_SUBMISSION_STATE.READY;
            },
            /**
             * Replace the input fields values that might be cached in the DOM
             */
            replaceCachedInputValues() {
                const hasDynamicFormFields = this.dynamicFormFieldsApiUrl && this.formId;
                const getElementByName = name => $(`[name=${ name }]`, this.$el);
                const replaceInputFieldsValues = fields => fields.forEach(({ name, value }) => {
                    const [el] = getElementByName(name);
                    if (el) {
                        el.value = value;
                    }
                });

                if (!hasDynamicFormFields) {
                    return;
                }

                axios.get(this.dynamicFormFieldsApiUrl, {
                    params: {
                        form: this.formId,
                    },
                })
                    .then(({ data }) => replaceInputFieldsValues(data))
                    /** We have currently no error strategies in case this call will fail ¯\_(ツ)_/¯ **/
                    .catch(console.error);
            },
        },
        mounted() {
            // IE emits wrong `input` events when mounting fields with prefilled data, leading to wrong dirty states
            setTimeout(() => {
                this.submissionState = FORM_SUBMISSION_STATE.READY;
            }, isIE ? 1000 : 0);

            // replace cached input values
            this.replaceCachedInputValues();
        },
        render() {
            return this.$scopedSlots.default(this.bindFormProps());
        },
    };
</script>
